"use client";
import { useMemo } from "react";

import { useFlags } from "@stadium/services/launch-darkly";
import { useQuery } from "@stadium/services/apollo-client";
import { permissionV1Flag } from "@stadium/services/launch-darkly/constants";

import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";

import { GetPackageListDocument } from "@stadium/hooks/graphql/operations";

import {
  serializeGraphqlFeaturePackagesData,
  serializeGraphqlUserPackageFeatureData,
} from "@stadium/helpers/src/onboarding-packages";

const defaultObj: { [key: string]: any } = {};

const defaultApiResponse = {
  packageList: [],
  packageFeatures: [],
};

const usePackage = ({ packageDetails, packagePermissions } = defaultObj) => {
  const { [permissionV1Flag]: permissionFlag } = useFlags();

  const { activePackage, permissions, loading } = useCurrentUserContext();

  const { data, loading: isPackageListLoading } = useQuery(GetPackageListDocument, {
    skip: packageDetails,
  });

  const { packageList: queryPackageList, packageFeatures: queryPackageFeatures } =
    data || defaultApiResponse;

  const packageList = packageDetails?.packageList || queryPackageList;
  const features = packageDetails?.packageFeatures || queryPackageFeatures;
  const isPermitted = packagePermissions || permissionFlag;
  const { packages, packageFeatures } = useMemo(
    () =>
      serializeGraphqlFeaturePackagesData({
        packages: packageList,
        packageFeatures: features,
        permissionFlag: isPermitted,
      }),
    [packagePermissions, packageList, features],
  );

  const newUserPackageData = useMemo(
    () =>
      serializeGraphqlUserPackageFeatureData({
        activePackage,
        packages,
        permissions,
        permissionFlag: isPermitted,
      }),
    [activePackage, packages, permissions, isPermitted],
  );

  return {
    packages,
    packageFeatures,
    userPackageData: newUserPackageData,
    loading,
    isPackageListLoading,
  };
};

export default usePackage;
